import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { Copy, QrCode, Trash2, ExternalLink, Loader2, Sparkles, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ShortenForm from "@/components/ShortenForm";
import QRDialog from "@/components/QRDialog";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import AuthDialog from "@/components/AuthDialog";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [qrLink, setQrLink] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);

  const load = async () => {
    try {
      const [links, an] = await Promise.all([
        api.get("/api/links"),
        api.get("/api/analytics/summary?days=30"),
      ]);
      setData(links.data);
      setAnalytics(an.data);
    } catch (e) {
      if (e?.response?.status === 401) setAuthOpen(true);
      else console.error(e);
    }
  };

  useEffect(() => { if (user) load(); }, [user]);

  useEffect(() => { if (!loading && !user) setAuthOpen(true); }, [loading, user]);

  const del = async (id) => {
    if (!window.confirm("Delete this link and all its clicks?")) return;
    try {
      await api.delete(`/api/links/${id}`);
      toast.success("Link deleted");
      load();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const copy = async (text) => {
    await navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const shortUrlFor = (slug) => `${data?.public_base_url || API_BASE}/${slug}`;

  if (!user) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-24 text-center">
        <h2 className="font-heading text-3xl font-light mb-4">Sign in to access your dashboard</h2>
        <p className="text-muted-foreground mb-6">Your links, QR codes and analytics live here.</p>
        <Button onClick={() => setAuthOpen(true)} data-testid="dashboard-signin-cta" className="rounded-none bg-foreground text-background hover:bg-foreground/90">Sign in</Button>
        <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      </div>
    );
  }

  if (!data || !analytics) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const usagePct = data.quota > 0 ? Math.min(100, ((data.total || 0) / data.quota) * 100) : 0;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-8 pt-4 md:pt-6 pb-10 md:pb-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
        <div className="lg:col-span-8">
          <div className="eyebrow text-muted-foreground mb-3">Dashboard</div>
          <h1 className="hero-title text-4xl md:text-5xl mb-2">Welcome back, <span className="text-accent">{user.name?.split(" ")[0] || user.email?.split("@")[0]}</span></h1>
          <p className="text-muted-foreground text-sm">Create a new short link, or manage the {data.total} you already have.</p>
        </div>
        <div className="lg:col-span-4 border border-border p-6 flex flex-col justify-between">
          <div>
            <div className="eyebrow text-muted-foreground mb-2">Plan</div>
            <div className="font-heading text-2xl font-light capitalize" data-testid="dashboard-plan">{data.plan}</div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs font-mono mb-1.5">
              <span>{data.total} / {data.quota}{data.pack_credits > 0 && ` + ${data.pack_credits} pack`}</span>
              <span className="text-muted-foreground">{Math.round(usagePct)}%</span>
            </div>
            <div className="h-1 bg-secondary">
              <div className="h-full bg-foreground" style={{ width: `${usagePct}%` }} data-testid="quota-progress" />
            </div>
            <Link to="/billing" data-testid="dashboard-billing-link" className="mt-4 inline-flex items-center text-xs font-medium link-underline">Manage billing <ArrowUpRight className="w-3 h-3 ml-1" /></Link>
          </div>
        </div>
      </div>

      {/* Create form */}
      <div className="mb-12">
        <ShortenForm isDemo={false} onCreated={() => load()} />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-border mb-10">
        <Stat label="Total clicks" value={analytics.total_clicks} testid="stat-clicks" />
        <Stat label="Links" value={analytics.total_links} testid="stat-links" />
        <Stat label="Clicks today" value={analytics.clicks_today} testid="stat-today" />
        <Stat label="Countries" value={Object.keys(analytics.countries || {}).length} testid="stat-countries" />
      </div>

      {/* Analytics charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
        <div className="lg:col-span-2 border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="eyebrow text-muted-foreground">Clicks · last 30 days</div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.clicks_by_day} margin={{ top: 10, right: 5, bottom: 0, left: -20 }}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} tickFormatter={(d) => d.slice(5)} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 0, fontSize: 12 }} />
                <Line type="monotone" dataKey="clicks" stroke="hsl(var(--accent))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="border border-border p-6">
          <div className="eyebrow text-muted-foreground mb-4">Devices</div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={Object.entries(analytics.devices || {}).map(([name, value]) => ({ name, value }))}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 0, fontSize: 12 }} />
                <Bar dataKey="value" fill="hsl(var(--foreground))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {Object.keys(analytics.countries || {}).length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="eyebrow text-muted-foreground mb-2">Top countries</div>
              <ul className="text-xs font-mono space-y-1">
                {Object.entries(analytics.countries).slice(0, 4).map(([c, n]) => (
                  <li key={c} className="flex justify-between"><span>{c}</span><span className="text-muted-foreground">{n}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Links table */}
      <div className="border border-border">
        <div className="p-4 md:p-6 border-b border-border flex items-center justify-between">
          <div>
            <div className="eyebrow text-muted-foreground mb-1">Your links</div>
            <div className="font-heading text-xl">{data.items.length} recent</div>
          </div>
        </div>
        {data.items.length === 0 ? (
          <div className="p-16 text-center text-muted-foreground text-sm" data-testid="links-empty">No links yet. Paste a URL above to create your first.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="p-4 eyebrow text-muted-foreground font-normal">Short</th>
                  <th className="p-4 eyebrow text-muted-foreground font-normal">Destination</th>
                  <th className="p-4 eyebrow text-muted-foreground font-normal">Clicks</th>
                  <th className="p-4 eyebrow text-muted-foreground font-normal">Created</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody data-testid="links-table-body">
                {data.items.map((l) => (
                  <motion.tr key={l.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="border-b border-border hover:bg-secondary/40 transition-colors" data-testid={`link-row-${l.id}`}>
                    <td className="p-4 font-mono">
                      <div className="flex items-center gap-2">
                        <a href={shortUrlFor(l.slug)} target="_blank" rel="noreferrer" className="hover:underline">/{l.slug}</a>
                        <button data-testid={`copy-link-${l.id}`} onClick={() => copy(shortUrlFor(l.slug))} className="opacity-40 hover:opacity-100 transition-opacity"><Copy className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                    <td className="p-4 max-w-xs truncate text-muted-foreground text-xs font-mono">{l.label && <span className="text-foreground mr-2">{l.label}</span>}{l.long_url}</td>
                    <td className="p-4 font-mono">{l.click_count || 0}</td>
                    <td className="p-4 text-xs text-muted-foreground font-mono">{l.created_at?.slice(0, 10)}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-1 justify-end">
                        <button data-testid={`qr-link-${l.id}`} onClick={() => setQrLink(l)} className="p-2 hover:bg-background border border-transparent hover:border-border" title="QR"><QrCode className="w-3.5 h-3.5" /></button>
                        <a data-testid={`open-link-${l.id}`} href={shortUrlFor(l.slug)} target="_blank" rel="noreferrer" className="p-2 hover:bg-background border border-transparent hover:border-border" title="Open"><ExternalLink className="w-3.5 h-3.5" /></a>
                        <button data-testid={`del-link-${l.id}`} onClick={() => del(l.id)} className="p-2 hover:bg-background border border-transparent hover:border-destructive text-destructive" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {qrLink && <QRDialog open={!!qrLink} onOpenChange={(o) => !o && setQrLink(null)} shortUrl={shortUrlFor(qrLink.slug)} linkId={qrLink.id} />}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="p-6 border-r border-border last:border-r-0" data-testid={testid}>
      <div className="eyebrow text-muted-foreground mb-2">{label}</div>
      <div className="font-heading text-3xl font-light tracking-tighter tabular-nums">{Number(value || 0).toLocaleString()}</div>
    </div>
  );
}
